import {memo, type KeyboardEvent} from 'react';
import parse from 'html-react-parser';
import Collapse from '@/components/atoms/Collapse';
import Icon from '@/components/atoms/Icon';
import type {SubpartAccordionItem} from '@/types';
import StructuredAnswer from './StructuredAnswer';
import styles from './AccordionItem.module.scss';

// Answers containing paragraphs, lists or tables can't live in a <p>, so the `soft` skin renders them in a <div>.
const BLOCK_ANSWER = /<(p|ul|ol|table)\b/i;

// The bordered skin keeps the MUI SimpleAccordion's ids and classes: globals.scss keys the open header's gradient on
// `.accordion-grid-main .Mui-expanded .panel1a-header`.
const SUMMARY_CLASS = 'panel1a-header';
const REGION_ID = 'panel1a-content';

type AccordionAnswer = string | SubpartAccordionItem['answer'];

interface AccordionItemProps {
    /** Plain text; the `bordered` skin also renders HTML in it (some questions bold a word). */
    question: string;
    /** HTML from the content files (links, lists), or a structured answer (`bordered` only). */
    answer: AccordionAnswer;
    expanded: boolean;
    onToggle: () => void;
    /**
     * `filled`: white panel, grey header that turns navy when open, plain +/− (the FAQ section).
     * `soft`: ice-blue rounded panel, +/− in a circle, rich answers allowed (the split FAQ beside the artwork).
     * `bordered`: white bordered card with a soft shadow, gradient header when open, structured answers allowed (the
     * one-column "Frequently Asked Questions" section).
     */
    variant?: 'filled' | 'soft' | 'bordered';
    /** `bordered` only: the header's id (the region is labelled by it). */
    id?: string;
}

const cx = (...names: Array<string | false | undefined>) => names.filter(Boolean).join(' ');

/** Keyboard behaviour of MUI's ButtonBase on a non-button element: Enter fires on keydown, Space on keyup. */
const toggleKeys = (onToggle: () => void) => ({
    onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === ' ') e.preventDefault();
        if (e.key === 'Enter') {
            e.preventDefault();
            onToggle();
        }
    },
    onKeyUp: (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget && e.key === ' ') onToggle();
    }
});

const BorderedItem = ({question, answer, expanded, onToggle, id}: AccordionItemProps) => {
    // `Mui-expanded` stays on the expanded elements: globals.scss keys the open header's gradient on it.
    const expandedClass = expanded && 'Mui-expanded';
    return (
        <div className={cx(styles.bordered, expandedClass, expanded && styles.expanded)}>
            <div
                className={cx(styles.borderedSummary, expandedClass, SUMMARY_CLASS)}
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                aria-controls={REGION_ID}
                id={id}
                onClick={onToggle}
                {...toggleKeys(onToggle)}
            >
                <div className={cx(styles.borderedSummaryContent, expandedClass)}>
                    <h3 className={cx('question font-regular-text', styles.borderedQuestion)}>{question ? parse(question) : ''}</h3>
                </div>
                <div className={cx(styles.borderedIconWrapper, expandedClass)}>
                    {expanded ? <Icon name="Remove" className={cx('remove-icon', styles.removeIcon)} /> : <Icon name="Add" />}
                </div>
            </div>
            <Collapse in={expanded}>
                <div aria-labelledby={id} id={REGION_ID} role="region">
                    {typeof answer === 'string' ? (
                        <div className={cx('muiAccordionDetails', styles.details)}>
                            <p className={cx('data_links answer font-regular-text', styles.text)}>{answer ? parse(answer) : ''}</p>
                        </div>
                    ) : (
                        <StructuredAnswer answer={answer} />
                    )}
                </div>
            </Collapse>
        </div>
    );
};

/**
 * One FAQ row: an <h3> question that toggles its answer. The answer stays in the HTML when closed (crawlable).
 */
const AccordionItem = (props: AccordionItemProps) => {
    const {question, answer, expanded, onToggle, variant = 'filled'} = props;
    if (variant === 'bordered') return <BorderedItem {...props} />;

    const soft = variant === 'soft';
    const html = typeof answer === 'string' ? answer : '';
    const answerClass = cx('font-regular-text remove-underline', styles.answer);
    const icon = (
        <Icon name={expanded ? 'Remove' : 'Add'} className={soft ? undefined : expanded ? styles.iconExpanded : styles.iconCollapsed} />
    );

    return (
        <div className={cx('font-headers', styles.root, styles[variant], expanded && styles.expanded)}>
            <div
                className={styles.summary}
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                onClick={onToggle}
                {...toggleKeys(onToggle)}
            >
                <div className={styles.summaryContent}>
                    <h3 className={styles.question}>{question}</h3>
                </div>
                <div className={styles.expandIconWrapper}>{soft ? <div className={styles.toggleIcon}>{icon}</div> : icon}</div>
            </div>
            <div className={styles.collapse} role="region">
                <div className={styles.collapseInner}>
                    <div className={styles.details}>
                        {soft && BLOCK_ANSWER.test(html) ? (
                            <div className={cx(answerClass, styles.richAnswer)}>{parse(html)}</div>
                        ) : (
                            <p className={answerClass}>{parse(html)}</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default memo(AccordionItem);
